import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CreditCard, Loader2, MessageCircle, Phone, Save, Trash2, Wallet } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";

/**
 * Modulo numeri WhatsApp operativi.
 * mode: "user" → /api/whatsapp-ops/bindings (+ crediti)
 * mode: "admin" → /api/admin/whatsapp-ops/bindings
 */
export default function WhatsAppOpsBindingsPanel({ mode = "user", authHeaders }) {
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [affidyaPhone, setAffidyaPhone] = useState("");
  const [wallet, setWallet] = useState(null);
  const [packages, setPackages] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [billingEnabled, setBillingEnabled] = useState(true);
  const [buyingId, setBuyingId] = useState(null);
  const creditVerifyDone = useRef(false);

  const api = process.env.REACT_APP_BACKEND_URL;
  const base =
    mode === "admin"
      ? `${api}/api/admin/whatsapp-ops/bindings`
      : `${api}/api/whatsapp-ops/bindings`;

  const loadCredits = useCallback(async () => {
    if (mode !== "user") return;
    try {
      const res = await fetch(`${api}/api/whatsapp-ops/credits`, { headers: authHeaders });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) return;
      setWallet(json.wallet || null);
      setPackages(json.packages || []);
      setLedger(json.ledger || []);
      setBillingEnabled(json.settings?.enabled !== false);
    } catch {
      /* non bloccante */
    }
  }, [api, authHeaders, mode]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(base, { headers: authHeaders });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Errore caricamento");
      const data = json.data || [];
      setRows(data);
      setAffidyaPhone(json.affidya_phone_display || json.affidya_phone || "");
      const next = {};
      data.forEach((row) => {
        next[row.user_id] = row.phone || "";
      });
      setDrafts(next);
      if (mode === "user") await loadCredits();
    } catch (e) {
      toast.error(e.message || "Errore");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, base, loadCredits, mode]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (mode !== "user" || creditVerifyDone.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("credit") !== "success") return;
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    creditVerifyDone.current = true;
    (async () => {
      try {
        const res = await fetch(`${api}/api/whatsapp-ops/credits/verify`, {
          method: "POST",
          headers: { ...authHeaders, "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.detail || "Verifica pagamento fallita");
        toast.success("Credito WhatsApp operativo accreditato");
        if (json.wallet) setWallet(json.wallet);
        await loadCredits();
      } catch (e) {
        toast.error(e.message || "Errore verifica pagamento");
      } finally {
        params.delete("credit");
        params.delete("session_id");
        const qs = params.toString();
        const next = `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash || ""}`;
        window.history.replaceState({}, "", next);
      }
    })();
  }, [api, authHeaders, loadCredits, mode]);

  const buyPackage = async (pkg) => {
    setBuyingId(pkg.id);
    try {
      const res = await fetch(`${api}/api/whatsapp-ops/credits/checkout`, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({ package_id: pkg.id }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Errore checkout");
      if (json.demo) {
        toast.success(json.message || "Credito accreditato (demo)");
        if (json.wallet) setWallet(json.wallet);
        await loadCredits();
        return;
      }
      if (json.checkout_url) {
        window.location.href = json.checkout_url;
        return;
      }
      throw new Error("URL di pagamento non disponibile");
    } catch (e) {
      toast.error(e.message || "Errore");
    } finally {
      setBuyingId(null);
    }
  };

  const save = async (row) => {
    const phone = (drafts[row.user_id] || "").trim();
    setSavingId(row.user_id);
    try {
      const res = await fetch(base, {
        method: "PUT",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({
          subject_id: row.user_id,
          phone,
          active: true,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Errore salvataggio");
      toast.success(phone ? "Numero salvato" : "Numero rimosso");
      load();
    } catch (e) {
      toast.error(typeof e.message === "string" ? e.message : "Errore");
    } finally {
      setSavingId(null);
    }
  };

  const remove = async (row) => {
    setSavingId(row.user_id);
    try {
      const res = await fetch(base, {
        method: "DELETE",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({ subject_id: row.user_id }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Errore eliminazione");
      toast.success("Numero rimosso");
      setDrafts((d) => ({ ...d, [row.user_id]: "" }));
      load();
    } catch (e) {
      toast.error(e.message || "Errore");
    } finally {
      setSavingId(null);
    }
  };

  const balanceLabel =
    wallet?.balance_eur != null ? `${Number(wallet.balance_eur).toFixed(2)} €` : "—";

  return (
    <div className="space-y-4">
      {mode === "user" && billingEnabled && (
        <Card className="card-base border-amber-200">
          <CardHeader className="p-4 md:p-6">
            <CardTitle className="flex items-center gap-2 text-base md:text-lg">
              <Wallet className="w-5 h-5 text-amber-600" />
              Credito AI WhatsApp operativo
            </CardTitle>
            <CardDescription className="text-xs md:text-sm">
              Solo i messaggi WhatsApp operativo consumano questo credito. Chat, vocali EVA e
              altre funzioni AI restano incluse nell&apos;abbonamento Affidya.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 md:p-6 pt-0 space-y-4">
            <div className="flex flex-wrap items-end justify-between gap-3 rounded-lg border border-amber-100 bg-amber-50/60 p-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-amber-800/70">Saldo disponibile</p>
                <p className="text-2xl font-semibold text-amber-950">{balanceLabel}</p>
              </div>
              {wallet?.free_credit_granted && (
                <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                  Credito iniziale attivo
                </Badge>
              )}
            </div>

            {packages.length > 0 && (
              <div className="grid gap-3 sm:grid-cols-3">
                {packages.map((pkg) => (
                  <div
                    key={pkg.id}
                    className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-white p-3"
                  >
                    <p className="font-medium text-slate-900">{pkg.name}</p>
                    <p className="text-lg font-semibold text-emerald-700">
                      {Number(pkg.price_eur).toFixed(2)} €
                    </p>
                    {pkg.description && (
                      <p className="text-xs text-slate-500 flex-1">{pkg.description}</p>
                    )}
                    <Button
                      size="sm"
                      className="mt-auto bg-emerald-600 hover:bg-emerald-700"
                      disabled={buyingId === pkg.id}
                      onClick={() => buyPackage(pkg)}
                    >
                      {buyingId === pkg.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4 mr-1.5" />
                          Acquista
                        </>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {ledger.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase text-slate-500 mb-2">Movimenti recenti</p>
                <ul className="text-sm space-y-1 max-h-40 overflow-y-auto">
                  {ledger.slice(0, 12).map((row) => {
                    const amt = Number(row.amount_display_eur || 0);
                    const label =
                      row.type === "purchase"
                        ? "Acquisto"
                        : row.type === "free_grant"
                          ? "Credito gratuito"
                          : row.type === "usage"
                            ? "Consumo"
                            : row.type;
                    return (
                      <li
                        key={row.id}
                        className="flex justify-between gap-2 text-slate-600 border-b border-slate-100 py-1"
                      >
                        <span className="truncate">
                          {label}
                          {row.note ? ` — ${row.note}` : ""}
                        </span>
                        <span className={amt < 0 ? "text-rose-600" : "text-emerald-700"}>
                          {amt >= 0 ? "+" : ""}
                          {amt.toFixed(2)} €
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="card-base border-emerald-200">
        <CardHeader className="p-4 md:p-6">
          <CardTitle className="flex items-center gap-2 text-base md:text-lg">
            <MessageCircle className="w-5 h-5 text-emerald-600" />
            WhatsApp operativo
          </CardTitle>
          <CardDescription className="text-xs md:text-sm space-y-2">
            <span className="block">
              Associa un numero WhatsApp agli utenti di{" "}
              <strong>Impostazioni → Utenti → Gestione Utenti</strong>. Scrivendo, inviando un
              vocale o un documento/foto al numero WhatsApp Affidya, l&apos;assistente riconosce
              il mittente ed esegue operazioni (con conferma) come in chat — incluso il
              caricamento di file dove Affidya lo prevede (documenti ospite, cambio terapia,
              fatture farmacia, allegati comunicazioni, ecc.).
            </span>
            {affidyaPhone ? (
              <span className="block font-medium text-emerald-800">
                Numero WhatsApp Affidya: {affidyaPhone}
              </span>
            ) : (
              <span className="block text-amber-700">
                Numero WhatsApp Affidya non ancora disponibile in configurazione.
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 md:p-6 pt-0 space-y-3">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
            </div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-slate-500 py-4 text-center">
              Nessun utente in Gestione Utenti. Crea prima gli utenti in Impostazioni → Utenti.
            </p>
          ) : (
            rows.map((row) => (
              <div
                key={row.user_id}
                className={`flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-lg border ${
                  row.is_active === false
                    ? "border-slate-200 bg-slate-50 opacity-60"
                    : "border-slate-200 bg-white"
                }`}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 truncate">{row.name || "—"}</p>
                  <p className="text-xs text-slate-500 truncate">{row.email}</p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {row.role && (
                      <Badge variant="outline" className="text-[10px]">
                        {row.role}
                      </Badge>
                    )}
                    {row.profile && (
                      <Badge variant="outline" className="text-[10px]">
                        {row.profile}
                      </Badge>
                    )}
                    {row.is_active === false && (
                      <Badge variant="outline" className="text-[10px] text-slate-500">
                        Disattivato
                      </Badge>
                    )}
                    {row.binding?.active && (
                      <Badge className="bg-emerald-100 text-emerald-800 text-[10px] hover:bg-emerald-100">
                        Numero associato
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <div className="relative flex-1 sm:w-48">
                    <Phone className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                    <Input
                      className="pl-8"
                      placeholder="+39 333…"
                      value={drafts[row.user_id] ?? ""}
                      onChange={(e) =>
                        setDrafts((d) => ({ ...d, [row.user_id]: e.target.value }))
                      }
                    />
                  </div>
                  <Button
                    size="sm"
                    onClick={() => save(row)}
                    disabled={savingId === row.user_id || row.is_active === false}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    {savingId === row.user_id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                  </Button>
                  {(row.binding || drafts[row.user_id]) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => remove(row)}
                      disabled={savingId === row.user_id}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
